import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TooltipDirective, TooltipModule } from 'ngx-bootstrap/tooltip';
import {
    MessageBoxButtons,
    MessageBoxExtensionService,
    MessageBoxResult,
    MessageBoxService,
} from '../UI-Lib/Message-Box/messagebox.service.upgrade';
import { ParcelActivityModalService } from '../Entity/Parcel/Info/Activity/parcelActivityModal.service';
import {
    AnnualDetailsNavigationEventService,
    AppealNavigationInput,
    AssessmentNavigationInput,
    TaxNavigationInput,
} from './annual-details-event.service';
import { AnnualDetailsNavigationService, NavigationState, ViewTarget } from './annual-details-navigation.service';
import { AnnualDetailsService } from './annual-details.service';
import {
    AnnualDetailAssessment,
    AnnualDetailComponent,
    AnnualDetailYear,
    AnnualDetailYearList,
} from './Annual-Year/annual-year.model';
import { AnnualAssessmentDetail, Assessment } from './Assessments/assessment.model';
import { AssessmentViewModel } from './Assessments/assessment.service';
import { FilingType, FilingTypeSummary } from './Compliance/compliance.model';
import { ComplianceService } from './Compliance/compliance.service';
import * as _ from 'lodash';
import { EntityType } from '../constants.new';
import { ForecastingBudgetingService } from '../Budget/forecasting.budgeting.service';
import { HelpService } from '../UI-Lib/Help-Tooltip';
import { ANNUAL_DETAILS_HELP } from './annual-details.component.help';
import { TimerService } from '../UI-Lib/Utilities';
import { CommonModule } from '@angular/common';
import { AddYearModalComponent } from './Modals/add-annual-year.component';
import { AssessmentTaxesDetailsIconComponent } from '../Common/Assessment-Taxes-Details/assessment-taxes-details-icon.component';
import { AnnualDetailsGridComponent } from './Grid/annual-details-grid.component';
import { AnnualYearComponent } from './Annual-Year/annual-year.component';
import { AppealComponent } from './Appeals/appeal.component';
import { TaxesComponent } from './Taxes/taxes.component';
import { AssessmentComponent } from './Assessments/assessment.component';
import { ComplianceComponent } from './Compliance/compliance.component';
import { AnnualBudgetComponent } from './Budget/budget.details.component';
import { ReferralsService } from '../Litigation/Referral/referrals.service';
import { AceLoaderComponent } from '../UI-Lib/ACE/Loader/loader.component';

@Component({
    selector: 'annual-details',
    templateUrl: './annual-details.component.html',
    imports: [
        AceLoaderComponent,
        CommonModule,
        TooltipModule,
        AddYearModalComponent,
        AssessmentTaxesDetailsIconComponent,
        AnnualDetailsGridComponent,
        AnnualYearComponent,
        AppealComponent,
        TaxesComponent,
        AssessmentComponent,
        ComplianceComponent,
        AnnualBudgetComponent,
    ],
})
export class AnnualDetailsComponent implements OnInit {
    constructor(
        private readonly annualDetailsService: AnnualDetailsService,
        private readonly annualDetailsNavigationService: AnnualDetailsNavigationService,
        private readonly complianceService: ComplianceService,
        private readonly messageBox: MessageBoxService,
        private readonly _messageBoxExtensionService: MessageBoxExtensionService,
        private readonly parcelActivityModalService: ParcelActivityModalService,
        private readonly navigationEvent: AnnualDetailsNavigationEventService,
        private readonly forecastingBudgetingService: ForecastingBudgetingService,
        private readonly _helpService: HelpService,
        private readonly _timer: TimerService,
        public toastr: ToastrService,
        private readonly _referralsService: ReferralsService,
    ) {}

    @Input() parcelId: number;
    @Input() entityTypeId: number;
    @Input() siteId: number;
    @Input() stateId: number;
    @Input() companyId: number;
    @Input() topLevelCompanyId: number;
    @Input() returnPreparationAllowed: boolean;
    @Input() assessorId: number;
    @Input() componentTypes: any[];
    @Input() hasWritePermission: boolean;
    @Input() hasCompanyCollectors: boolean;
    @Input() hasComplianceResponsibility: boolean;
    @Input() parcelAcctNum: string;
    @Input() masterParcelId: number;
    @Input() parcelLinkageTypeId: number;
    @Input() consolidatingTypeId: number;
    @Input() consolidatedParcelId: number;
    @Input() propertyType: number;
    @Input() annualYearData: Core.AnnualGridYearsInfo;
    @Output() maximizeChanged: EventEmitter<boolean> = new EventEmitter();
    @ViewChild('aDValidation') aDValidation: TooltipDirective;
    @ViewChild(AddYearModalComponent) addYearModal: AddYearModalComponent;

    components: AnnualDetailComponent[];
    filingTypes: FilingType[] = [];
    filings: FilingTypeSummary[] = [];
    years: AnnualDetailYear[];
    annualYearIDs: number[];
    maximize: boolean;
    navigationState: NavigationState;
    ViewTarget = ViewTarget;
    yearList: AnnualDetailYearList;
    vanillaYears: number[]; // For assessment-taxes-details modal
    totalYears: number = 0;
    annualDetailsSaveAllowed: boolean = true;
    budgetsEnabled: boolean = false;
    isViewTaxableValuesEnabled: boolean = true;
    canSaveWithAddToLitigationReferral: boolean = false;
    canRemoveAddToLitigationReferralTask: boolean = false;

    // *** Navigation functions ***
    // We originally didn't have these, prefering instead to do something like:
    //     [gotoGridHandler]="navigationState.goToGrid"
    // in the bindings; however we discovered that the "this" variable in the
    // goToGrid function isn't set to the navigationState in that case, causing
    // breakage. Using these shims in the component solves the problem.
    goToGrid: () => Promise<void>;
    goToAssessment: (year: AnnualDetailYear, revision: AnnualDetailAssessment) => Promise<AssessmentViewModel>;
    // *** End navigation functions ***

    async ngOnInit() {
        this._helpService.setContent(ANNUAL_DETAILS_HELP);
        this.maximize = false;
        this.navigationState = null;
        //this.navigationEvent.resetAnnualDetails();

        this.complianceService.getFilingTypesByParcel(this.parcelId).then(async (data) => {
            this.filingTypes = data;
            // It's possible that we're loading the page with a pre-selected annual year that's outside the
            // first 10-year range; if that happens, we need to default to showing all years
            const preLoadAnnualYearID = +this.getParameterValue('annualYearID');
            await this.getYearData(false, preLoadAnnualYearID, this.annualYearData);
            this.checkNavigationParameters();
        });

        this.goToGrid = () => this.navigationState.goToGrid();
        this.goToAssessment = (year: AnnualDetailYear, revision: AnnualDetailAssessment) =>
            this.navigationState.goToAssessment(year, revision);

        const goToGridFromEvent = async (annualYearID: number, cb: (year: AnnualDetailYear) => void) => {
            const hasYear = await this.ensureYearLoaded(annualYearID);
            if (!hasYear) return;

            const year = _.find(this.years, { annualYearID });
            if (year) {
                await this.navigationState.goToGrid();
                cb(year);
            }
        };
        this.navigationEvent.assessmentEvent$.forEach((event: AssessmentNavigationInput) => {
            goToGridFromEvent(event.annualYearID, (year: AnnualDetailYear) => {
                const assessment = _.find(year.annualGridDetails, { annualAssessmentID: event.annualAssessmentID });
                this.navigationState.goToAssessment(year, assessment);
            });
        });

        this.navigationEvent.appealEvent$.forEach((event: AppealNavigationInput) => {
            goToGridFromEvent(event.annualYearID, (year: AnnualDetailYear) => {
                const assessment = _.find(year.annualGridDetails, { annualAssessmentID: event.annualAssessmentID });
                this.navigationState.goToAppeals(year, assessment, event.appealID);
            });
        });

        this.navigationEvent.taxEvent$.forEach((event: TaxNavigationInput) => {
            goToGridFromEvent(event.annualYearID, (year: AnnualDetailYear) => {
                this.navigationState.goToTaxes(year, event.taxID);
            });
        });

        this.navigationEvent.refundEvent$.forEach((event) => {
            goToGridFromEvent(event.annualYearID, (year: AnnualDetailYear) => {
                this.navigationState.goToTaxes(year, null, event.taxID);
            });
        });

        this.navigationEvent.filingEvent$.forEach((event) => {
            goToGridFromEvent(event.annualYearID, (year: AnnualDetailYear) => {
                this.navigationState.goToCompliance(year, event.filingId);
            });
        });

        this.navigationEvent.refreshAnnualYearEvent$.forEach(async () => {
            const year = this.navigationState.currentYear;

            if (!year) {
                return;
            }

            const currentRevision = this.navigationState.currentRevision;

            await this.getYearData(false, year.annualYearID);
            const newYearData: AnnualDetailYear = _.find(this.years, { annualYearID: year.annualYearID });
            this.navigationState.currentYear = newYearData;
            if (this.navigationState.currentAssessmentVM) {
                this.navigationState.currentAssessmentVM.currentYear = newYearData;
            }

            this.navigationState.currentRevision = _.find(newYearData.annualGridDetails, {
                annualAssessmentID: currentRevision.annualAssessmentID,
            });
        });

        this.navigationEvent.goToGridEvent$.forEach(() => {
            this.navigationState.refreshGrid = true;
            this.navigationState.goToGrid();
        });

        this.navigationEvent.addYearRequestEvent$.forEach(async (request) => {
            try {
                await this.showAddYearModal(request.year);
            } finally {
                request.resolve();
            }
        });

        this.budgetsEnabled = await this.forecastingBudgetingService.getAreBudgetsEnabled(
            EntityType.Parcel,
            this.parcelId,
        );
    }

    annualDetailsSaveAllowedChanged(isAnnualDetailsSaveAllowed: boolean): void {
        this.annualDetailsSaveAllowed = isAnnualDetailsSaveAllowed;
    }

    ensureYearLoaded(annualYearID: number): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (!annualYearID || _.some(this.years, (y) => y.annualYearID === annualYearID)) {
                resolve(true);
            } else if (_.some(this.annualYearIDs, (i) => i === annualYearID)) {
                console.log(['Reloading annual years', this.years, annualYearID]);
                this.getYearData(true).then(() => {
                    resolve(true);
                });
            } else {
                resolve(false);
            }
        });
    }

    maximize_click(): void {
        this.maximize = true;
        this.maximizeChanged.emit(this.maximize);
    }

    unmaximize_click(): void {
        this.maximize = false;
        this.maximizeChanged.emit(this.maximize);
    }

    checkNavigationParameters(): void {
        //Read parameters
        //Find year to load from years, by ID. If none found, do nothing
        const annualYearParamID = +this.getParameterValue('annualYearID');
        if (annualYearParamID) {
            this._setNavigationBasedOnAnnualYearId(annualYearParamID);
        }

        const taxYearParam = +this.getParameterValue('taxYear');
        if (taxYearParam) {
            this._setNavigationBasedOnTaxYear(taxYearParam);
        }
    }

    async getYearData(
        getAllYears: boolean,
        searchForYear?: number,
        annualYearData?: Core.AnnualGridYearsInfo,
    ): Promise<void> {
        const annualGridYearsInfo =
            annualYearData || (await this.annualDetailsService.getAnnualYears(this.parcelId, getAllYears));

        this.annualYearIDs = annualGridYearsInfo.annualYearIDs;
        const callback = ({ totalYears, years }) => {
            this.totalYears = totalYears;
            this.components = this.annualDetailsService.getComponentsFromYears(years);
            this.filings = this.getFilingTypesFromYears(years);
            this.years = this.annualDetailsService.formatYearDateDisplay(years);
            this.yearList = new AnnualDetailYearList(this.years);
            this.navigationEvent.setYears(this.yearList.years.map((y) => y.annualYear1));
            this.vanillaYears = _.map(this.years, 'annualYear1');
            if (!this.navigationState) {
                this.navigationState = this.annualDetailsNavigationService.createNavigationState(
                    years,
                    this.parcelId,
                    this.masterParcelId,
                    this.parcelLinkageTypeId,
                    this.consolidatingTypeId,
                    this.consolidatedParcelId,
                    this.parcelAcctNum,
                    this.stateId,
                    this.assessorId,
                    this.hasWritePermission,
                );
            }
            this.navigationState.entityLoading = false;
            this.navigationState.resetYearsHandler = async () => {
                this.filingTypes = await this.complianceService.getFilingTypesByParcel(this.parcelId);
                await this.getYearData(false);
                const currentYear = this.years.find(
                    (x) => x.annualYearID === this.navigationState.currentYear?.annualYearID,
                );
                if (currentYear) {
                    this.navigationState.currentYear = currentYear;
                }
            };
            this.navigationState.staleAppealSavings = false;
            this.navigationState.staleAppealStatus = false;
        };

        // We'll go get the annual year data, and in the very rare case where we've loaded the page with a link to a year that's
        // not in the first 10 years, repeat the call and get all years before continuing (WK-4700)
        if (
            searchForYear &&
            !_.some(annualGridYearsInfo.years, (y) => y.annualYearID === searchForYear) &&
            _.some(this.annualYearIDs, (i) => i === searchForYear)
        ) {
            //console.log(['Extending annual year search', annualGridYearsInfo.years, searchForYear]);
            return this.annualDetailsService.getAnnualYears(this.parcelId, true).then(callback);
        }

        return callback(annualGridYearsInfo);
    }

    getFilingTypesFromYears(years: AnnualDetailYear[]): FilingTypeSummary[] {
        return _.chain(years)
            .map('complianceFilingTypeSummaries')
            .flatten()
            .uniqBy('filingTypeId')
            .compact()
            .sortBy('filingTypeSequence')
            .value() as FilingTypeSummary[];
    }

    toggleEdit(): void {
        this.navigationState.setEditMode(!this.navigationState.editMode);
    }

    saveEdit(): void {
        this.navigationState.validationHandler(async (isValid, errorMessage) => {
            if (!isValid) {
                this.navigationState.validationMessage = errorMessage;

                // Some validators (e.g. taxable values) show their own toastr instead and pass an
                // empty message here - skip the tooltip in that case so an empty bubble isn't shown.
                if (errorMessage) {
                    // HACK need setTimeout for some reason as when the 'show'
                    // is called the validationMessage is not set unless we have the timeout
                    this._timer.setTimeout(() => {
                        this.aDValidation.show();
                    });
                }

                return;
            }

            this.navigationState.validationMessage = '';
            this.canRemoveAddToLitigationReferralTask = false;

            if (
                this.navigationState.viewTarget === ViewTarget.appeal &&
                this.navigationState.currentAppealVM?.currentAppeal
            ) {
                const appealVM = this.navigationState.currentAppealVM;
                const appealLevel = appealVM.stateAppealLevels?.find(
                    (level) => level.appealLevelID === appealVM.currentAppeal.appealLevelID,
                );
                const isLitigation = !!appealLevel?.litigation;
                const isRyanLegalServices = appealVM.currentAppeal.isRyanLegalServices;
                let areLitigationReferralsAllowed = false;
                let isLitigationReferralTaskPresent = false;
                let areAnyTasksCompleted = false;
                let isAssignedToLitigationReferral = false;

                try {
                    const litigationReferralStatus = await this._referralsService.getAppealLitigationReferralStatus(
                        appealVM.currentAppeal.appealID,
                    );
                    areLitigationReferralsAllowed = !!litigationReferralStatus?.areLitigationReferralsAllowed;
                    isLitigationReferralTaskPresent = !!litigationReferralStatus?.isLitigationReferralTaskPresent;
                    areAnyTasksCompleted = !!litigationReferralStatus?.areAnyTasksCompleted;
                    isAssignedToLitigationReferral = !!litigationReferralStatus?.isAssignedToLitigationReferral;
                } catch {
                    areLitigationReferralsAllowed = false;
                    isLitigationReferralTaskPresent = false;
                    areAnyTasksCompleted = false;
                    isAssignedToLitigationReferral = false;
                }

                if (isRyanLegalServices) {
                    // RLS is checked: offer to add Litigation Referral task
                    const canAddLitigationReferralTask =
                        areLitigationReferralsAllowed &&
                        !isLitigationReferralTaskPresent &&
                        !areAnyTasksCompleted &&
                        !isAssignedToLitigationReferral;

                    const saveWithRLSmsg =
                        isLitigation && canAddLitigationReferralTask
                            ? 'Yes — Save the appeal and add the "Add to Litigation Referral" task if it does not already exist.\n' +
                              'No — Save the appeal only.\n' +
                              'Cancel — Return without saving.'
                            : null;

                    if (saveWithRLSmsg) {
                        const result = await this._messageBoxExtensionService.open({
                            title: 'Confirm',
                            message: saveWithRLSmsg,
                            buttons: MessageBoxButtons.YesNoCancel,
                        });

                        if (result == MessageBoxResult.Yes) {
                            this.canSaveWithAddToLitigationReferral = true;
                        } else if (result == MessageBoxResult.No) {
                            this.canSaveWithAddToLitigationReferral = false;
                        } else {
                            return;
                        }
                    }
                } else {
                    // RLS is unchecked: prevent removal if tied to litigation referral
                    if (!isLitigationReferralTaskPresent) {
                        this.canRemoveAddToLitigationReferralTask = false;
                    } else {
                        this.canRemoveAddToLitigationReferralTask =
                            areLitigationReferralsAllowed &&
                            isLitigationReferralTaskPresent &&
                            !areAnyTasksCompleted &&
                            !isAssignedToLitigationReferral;

                        if (!this.canRemoveAddToLitigationReferralTask) {
                            const result = await this._messageBoxExtensionService.open({
                                message:
                                    ' The "Add to Litigation Referral" task cannot be removed with RLS unchecked. It has been completed or the Appeal is currently assigned to litigation referral. Do you want to continue to save?',
                                buttons: MessageBoxButtons.OKCancel,
                            });

                            if (result === MessageBoxResult.Cancel) {
                                return;
                            }
                        }
                    }
                }

                // Pass workflow intent through the appeal model rather than query params.
                appealVM.currentAppeal.canInsertAddToLitigationReferralTask = this.canSaveWithAddToLitigationReferral;
                appealVM.currentAppeal.canRemoveAddToLitigationReferralTask = this.canRemoveAddToLitigationReferralTask;
            }
            this.canSaveWithAddToLitigationReferral = false;

            if (this.navigationState.refreshGrid) {
                const toastMessage = await this.navigationState.saveHandler();

                if (toastMessage) {
                    this.toastr.info(toastMessage);
                }

                await this.getYearData(false);

                this.navigationState.editMode = false;
                this.navigationState.setDirty(false);
                this.navigationState.refreshGrid = false;
            } else {
                const toastMessage = await this.navigationState.saveHandler();
                if (toastMessage) {
                    this.toastr.info(toastMessage);
                }

                this.navigationState.editMode = false;
                this.navigationState.setDirty(false);
            }

            await this.promptToAddYear();
        });
    }

    // Shows the Add Year dialog when the save that just completed did not create the subsequent Tax Year.
    private async promptToAddYear(): Promise<void> {
        const prompt = this.navigationState.consumeAddYearPrompt();

        if (prompt) {
            try {
                // The save just changed the year the dialog inherits from, so reload the years it reads its setup from.
                await this.navigationState.resetYearsHandler();
            } finally {
                // The server validates when the year is created, so the dialog is shown even if the reload failed.
                await this.showAddYearModal(prompt.annualYear);
            }
        }
    }

    private async showAddYearModal(year: number): Promise<void> {
        await this.addYearModal?.open(year);
    }

    async cancelEdit(): Promise<void> {
        const performCancel = (): void => {
            this.navigationState.cancelHandler();
            this.navigationState.editMode = false;
            this.navigationState.setDirty(false);
        };

        if (this.navigationState.isDirty) {
            const result = await this.messageBox.open({
                title: 'WARNING',
                message: 'Are you sure you wish to cancel your changes?',
                buttons: MessageBoxButtons.YesNo,
            });

            if (result === MessageBoxResult.Yes) {
                performCancel();
            }
        } else {
            performCancel();
        }
    }

    getParameterValue(name: string): any {
        const path = window.location.href;
        const parsedName = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp(`[?&]${parsedName}(=([^&#]*)|&|#|$)`),
            results = regex.exec(path);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }

        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    assessmentModelChange(changedAssessment: Assessment): void {
        if (this.navigationState.currentRevision.annualAssessmentID === changedAssessment.annualAssessmentID) {
            //If the name has changed of the
            this.navigationState.currentRevision.revisionDesc = changedAssessment.revisionDesc;
            this.navigationState.currentRevision.taxableValues = changedAssessment.taxableValues;

            //Update each component by hand
            changedAssessment.annualAssessmentDetails.forEach(
                ({
                    assessmentClassComponentID,
                    fairMarketValue,
                    componentName,
                    assessmentComponentTypeID,
                    efAction,
                }: AnnualAssessmentDetail) => {
                    let changedComponent: AnnualDetailComponent = _.find(
                        this.navigationState.currentRevision.annualGridComponents,
                        {
                            assessmentClassComponentID,
                        },
                    );

                    if (changedComponent) {
                        changedComponent.fairMarketValue = fairMarketValue;
                    }

                    if (efAction === 'add') {
                        //If there's no existing component in navigation state, add it
                        //This goes off of componentName because there are no IDs yet since it's not saved
                        if (
                            !changedComponent &&
                            !_.some(this.navigationState.currentRevision.annualGridComponents, { componentName })
                        ) {
                            this.navigationState.currentRevision.annualGridComponents.push(
                                new AnnualDetailComponent(componentName, fairMarketValue, assessmentComponentTypeID),
                            );
                        } else {
                            changedComponent = _.find(this.navigationState.currentRevision.annualGridComponents, {
                                componentName,
                            });
                            changedComponent.fairMarketValue = fairMarketValue;
                        }
                    }
                },
            );

            //Delete components missing from the input
            //Using name here in case we are deleting an un-committed component (hence no ID)
            //Reverse for loop so we can safely splice out the "current" item without upsetting the index order
            for (let i = this.navigationState.currentRevision.annualGridComponents.length - 1; i >= 0; i--) {
                const componentExists = _.some(changedAssessment.annualAssessmentDetails, {
                    componentName: this.navigationState.currentRevision.annualGridComponents[i].componentName,
                });
                if (!componentExists) {
                    this.navigationState.currentRevision.annualGridComponents.splice(i, 1);
                }
            }
        }
    }

    openParcelActivityModal(): void {
        this.parcelActivityModalService.open(this.parcelId, this.siteId, _.map(this.years, 'annualYear1') as number[]);
    }

    private _setNavigationBasedOnAnnualYearId(annualYearID: number): void {
        const year = _.find(this.years, { annualYearID });

        if (!year) {
            return;
        }

        const annualAssessmentID = +this.getParameterValue('annualAssessmentID');

        if (annualAssessmentID) {
            const revision = _.find(year.annualGridDetails, { annualAssessmentID });
            const appealID = +this.getParameterValue('appealID');

            if (appealID) {
                this.navigationState.goToAppeals(year, revision, appealID);
            } else {
                this.navigationState.goToAssessment(year, revision);
            }
        } else if (this.getParameterValue('billClusterID')) {
            this.navigationState.goToTaxes(year, +this.getParameterValue('billClusterID'));
        } else if (this.getParameterValue('refundID')) {
            this.navigationState.goToTaxes(year, null, +this.getParameterValue('refundID'));
        } else if (this.getParameterValue('filingId')) {
            this.navigationState.goToCompliance(year, +this.getParameterValue('filingId'));
        }
    }

    private _setNavigationBasedOnTaxYear(taxYear: number): void {
        const year = _.find(this.years, { annualYear1: taxYear });

        if (year) {
            this.navigationState.goToCompliance(year);
        }
    }
}
